import { format, parseISO } from "date-fns"
import PropTypes from "prop-types"
import React, { useState } from "react"
import { useQuery } from "react-query"

import Badge from "src/components/Badge"
import { statusToken } from "src/components/Badge/status_tokens"
import DataTable from "src/components/DataTable"
import Form from "src/components/Form"
import OverflowMenu from "src/components/OverflowMenu"

import { getDromoImportRows } from "src/api/DromoImports"

import { capitalize, humanize } from "src/utils/string_helpers"

import RowDataModal from "./RowDataModal"

const formatTimestamp = (isoString) => {
  const timestamp = parseISO(isoString)
  return format(timestamp, "MMM d, yyyy 'at' h:mm a")
}

export default function ImportDetails({ dromoImport, statuses }) {
  const [page, setPage] = useState(1)
  const [selectedStatuses, setSelectedStatuses] = useState([])
  const [viewingRow, setViewingRow] = useState(null)

  const { data, isLoading } = useQuery({
    queryKey: ["dromoImportRows", dromoImport.id, page, selectedStatuses],
    queryFn: () =>
      getDromoImportRows({
        dromoImportId: dromoImport.id,
        page,
        statuses: selectedStatuses,
      }),
  })

  const handleStatusCheckboxChange = (status, checked) => {
    const updated = checked
      ? [...selectedStatuses, status]
      : selectedStatuses.filter((s) => s !== status)
    setSelectedStatuses(updated)
    setPage(1)
  }

  const handlePageChange = ({ selected }) => {
    setPage(selected)
  }

  const handleRowLinkClick = (url) => {
    window.open(url, "_blank")
  }

  const getLinksForRow = (row) => {
    return row.links.map(({ cta, url }, index) => {
      return (
        <p className="my-0" key={`${row.id}-link-${index}`}>
          <a className="cursor-pointer" onClick={() => handleRowLinkClick(url)}>
            {cta}
          </a>
        </p>
      )
    })
  }

  const colDefs = [
    {
      key: "rowNumber",
      header: "Row #",
      width: "10%",
      render: (row) => row.rowNumber,
    },
    {
      key: "status",
      header: "Status",
      width: "15%",
      render: (row) => (
        <Badge text={capitalize(row.status)} color={statusToken(row.status)} />
      ),
    },
    {
      key: "related",
      header: "Link(s)",
      width: "35%",
      render: (row) => getLinksForRow(row),
    },
    {
      key: "errorMessage",
      header: "Error Message",
      render: (row) => row.errorMessage || "-",
    },
  ]

  return (
    <div className="mx-auto mt-4 max-w-7xl px-4">
      <div className="mb-6">
        <div className="mb-2 flex items-baseline gap-4">
          <h1 className="text-2xl font-semibold">
            {capitalize(humanize(dromoImport.importType))} Import
          </h1>
          <Badge
            text={capitalize(dromoImport.status)}
            color={statusToken(dromoImport.status)}
          />
        </div>
        <div className="text-gray-600">
          Started {formatTimestamp(dromoImport.createdAt)} &middot;{" "}
          {dromoImport.completedRowsCount} / {dromoImport.numRows} rows
          completed
        </div>
      </div>

      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold">Rows</h2>
        <div className="shrink-0">
          <OverflowMenu menuButtonLabel="Status">
            <div className="space-y-2 p-4">
              {statuses.map((status) => (
                <Form.Checkbox
                  key={status}
                  name={status}
                  label={capitalize(status)}
                  checked={selectedStatuses.includes(status)}
                  onChange={(e) =>
                    handleStatusCheckboxChange(status, e.target.checked)
                  }
                />
              ))}
            </div>
          </OverflowMenu>
        </div>
      </div>
      <DataTable
        name="dromoImportRows"
        rowData={data?.rows || []}
        colDefs={colDefs}
        isLoading={isLoading}
        showBlankRows={isLoading}
        rowCount={10}
        fullHeight
        autoColumnWidth
        pagination
        page={page}
        numberOfPages={data?.totalPages || 1}
        onPageChange={handlePageChange}
        rowConfig={{
          actions: (row) => [
            {
              action: "View details",
              onClick: () => setViewingRow(row),
            },
          ],
        }}
      />

      <RowDataModal row={viewingRow} onClose={() => setViewingRow(null)} />
    </div>
  )
}

ImportDetails.propTypes = {
  dromoImport: PropTypes.shape({
    id: PropTypes.number.isRequired,
    importType: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    numRows: PropTypes.number.isRequired,
    completedRowsCount: PropTypes.number.isRequired,
    createdAt: PropTypes.string.isRequired,
  }).isRequired,
  statuses: PropTypes.arrayOf(PropTypes.string).isRequired,
}
